import type { PostHog } from 'posthog-js';
import type { MouseEvent } from 'react';
import type { NavigateFunction } from 'react-router-dom';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const POSTHOG_ENABLED = process.env.REACT_APP_POSTHOG_ENABLE === 'true';

let posthogInstance: PostHog | null = null;
let initStarted = false;

export type CtaProduct = 'website' | 'assessment' | 'my_coach' | 'general';
export type AppSurface = 'candidate' | 'business';

export type HeroView = 'assessment' | 'my_coach';

export interface CtaProperties {
	cta_label: string;
	product: CtaProduct;
	location: string;
	page?: string;
	app_surface?: AppSurface;
	destination_domain?: string;
	hero_view?: HeroView;
}


export const initPostHog = (): void => {
	if (initStarted || !POSTHOG_KEY || !POSTHOG_ENABLED) return;
	initStarted = true;

	import('posthog-js').then(({ default: posthog }) => {
		posthog.init(POSTHOG_KEY as string, {
			api_host: POSTHOG_HOST,
			capture_pageview: 'history_change',
			autocapture: true
		});
		posthog.register({ product: 'website' });
		posthogInstance = posthog;
	});
};

export const trackCta = (eventName: string, properties: CtaProperties): void => {
	if (!posthogInstance) return;
	posthogInstance.capture(eventName, {
		page: window.location.pathname,
		...properties
	});
};

export const trackHeroViewSwitched = (from_view: HeroView, to_view: HeroView, method: 'click' | 'keyboard'): void => {
	if (!posthogInstance) return;
	posthogInstance.capture('hero view switched', {
		page: window.location.pathname,
		from_view,
		to_view,
		method
	});
};

// BE-001 demo-request funnel: a dedicated, minimal taxonomy (product_interest +
// source_page only) separate from the general-purpose CtaProperties above.
export type DemoFunnelEvent = 'form open' | 'submit attempt' | 'submit success' | 'submit failure';

export interface DemoFunnelProperties {
	product_interest: string;
	source_page: string;
}

export const trackDemoFunnel = (eventName: DemoFunnelEvent, properties: DemoFunnelProperties): void => {
	if (!posthogInstance) return;
	posthogInstance.capture(eventName, properties);
};

export const decorateCrossDomainUrl = (url: string): string => {
	if (!posthogInstance) return url;

	try {
		const target = new URL(url, window.location.origin);
		if (target.hostname === window.location.hostname) return url;

		const distinctId = posthogInstance.get_distinct_id();
		if (!distinctId) return url;

		target.searchParams.set('ph_distinct_id', distinctId);
		return target.toString();
	} catch {
		return url;
	}
};

export const getDestinationDomain = (url: string): string => {
	try {
		return new URL(url, window.location.origin).hostname;
	} catch {
		return url;
	}
};

export const decorateAnchorOnClick = (e: MouseEvent<HTMLAnchorElement>): void => {
	e.currentTarget.href = decorateCrossDomainUrl(e.currentTarget.href);
};


export const createTrackedAnchorOnClick =
	(eventName: string, properties: CtaProperties) =>
	(e: MouseEvent<HTMLAnchorElement>): void => {
		trackCta(eventName, properties);
		e.currentTarget.href = decorateCrossDomainUrl(e.currentTarget.href);
	};


export const trackAndNavigateCrossDomain = (eventName: string, properties: CtaProperties, url: string): void => {
	trackCta(eventName, properties);
	window.location.assign(decorateCrossDomainUrl(url));
};


export const trackDemoRequestAndNavigate = (
	navigate: NavigateFunction,
	properties: Pick<CtaProperties, 'cta_label' | 'product' | 'location'>
): void => {
	trackCta('demo requested', properties);
	navigate('/book-a-demo');
};
